import { Email, Person } from "@mui/icons-material"
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid"
import { deDE } from "@mui/x-data-grid/locales"
import { StyledButton } from "components/Button/StyledButton"
import { Employee, User } from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { useEffect, useState } from "react"

type AddProjectManagerDialogProps = {
  open: boolean
  onClose: () => void
  currentManager?: User | null
  onSave: (_manager: User) => void
}

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ justifyContent: "space-between" }}>
      <GridToolbarQuickFilter debounceMs={300} placeholder="Mitarbeiter suchen..." />
    </GridToolbarContainer>
  )
}

export const AddProjectManagerDialog = (props: AddProjectManagerDialogProps) => {
  const { open, onClose, onSave, currentManager } = props

  const { employees } = useStoreState((state) => state)
  const { fetchEmployees } = useStoreActions((action) => action)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)

  useEffect(() => {
    const loadEmployees = async () => {
      try {
        setLoading(true)
        setError(null)
        await fetchEmployees({ fresh: false })
      } catch (err) {
        console.error("Error loading employees:", err)
        setError("Fehler beim Laden der Mitarbeiter.")
      } finally {
        setLoading(false)
      }
    }
    if (open && (!employees || employees.length === 0)) {
      loadEmployees()
    } else {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleRowClick = (params: GridRowParams<Employee>) => {
    setSelectedEmployee(params.row)
  }

  const handleSave = () => {
    if (selectedEmployee) {
      // Convert Employee to User format
      const manager: User = {
        id: selectedEmployee.id,
        displayName: selectedEmployee.EmployeeName || "",
        email: selectedEmployee.ContactInfo?.Email || "",
        userPrincipalName: selectedEmployee.ContactInfo?.Email || "",
      }
      onSave(manager)
    }
    onClose()
    setSelectedEmployee(null)
  }

  const handleRemoveManager = () => {
    // Save with empty user to remove the manager
    const emptyManager: User = {
      id: "",
      displayName: "",
      email: "",
      userPrincipalName: "",
    }
    onSave(emptyManager)
    onClose()
  }

  const columns: GridColDef<Employee>[] = [
    {
      field: "avatar",
      headerName: "",
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Avatar sx={{ width: 32, height: 32 }}>
          {params.row.EmployeeName?.charAt(0) || <Person />}
        </Avatar>
      ),
    },
    {
      field: "EmployeeName",
      headerName: "Name",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography variant="body2">{params.row.EmployeeName || "N/A"}</Typography>
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "E-Mail",
      flex: 2,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            height: "100%",
          }}
        >
          {params.row.ContactInfo?.Email && (
            <>
              <Email fontSize="small" color="action" />
              <Typography variant="body2">{params.row.ContactInfo.Email}</Typography>
            </>
          )}
        </Box>
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 120,
      renderCell: (params: GridRenderCellParams<Employee>) => (
        <Typography
          variant="body2"
          color={params.row.Status === "Active" ? "success.main" : "text.secondary"}
        >
          {params.row.Status || ""}
        </Typography>
      ),
    },
  ]

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            height: "80vh",
          },
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Projektleiter auswählen</Typography>
          {currentManager?.displayName && (
            <Typography variant="body2" color="text.secondary">
              Aktuell: {currentManager.displayName}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <DataGrid
          rows={employees || []}
          columns={columns}
          loading={loading}
          onRowClick={handleRowClick}
          getRowId={(row) => row.id}
          slots={{
            toolbar: CustomToolbar,
          }}
          sortModel={[{ field: "EmployeeName", sort: "asc" }]}
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[10, 25, 50]}
          sx={{
            "& .MuiDataGrid-row": {
              cursor: "pointer",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "action.hover",
            },
            "& .MuiDataGrid-row.Mui-selected": {
              backgroundColor: "action.selected",
            },
          }}
        />
      </DialogContent>
      <DialogActions>
        {currentManager?.id && (
          <StyledButton
            buttonType="custom"
            customText="Projektleiter entfernen"
            onClick={handleRemoveManager}
            sx={{ mr: "auto" }}
          />
        )}
        <StyledButton buttonType="cancel" onClick={onClose} />
        <StyledButton
          buttonType="custom"
          customText="Auswählen"
          onClick={handleSave}
          disabled={!selectedEmployee}
        />
      </DialogActions>
    </Dialog>
  )
}
